import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, MapPin, Phone, Globe, Clock, Navigation, BadgeCheck, Plus, ShieldCheck, Info, Building2 } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";

const TYPES = [
  { v: "all", l: "All services" }, { v: "peer_support", l: "Peer support" },
  { v: "nhs_service", l: "NHS drug & alcohol" }, { v: "private_rehab", l: "Private rehab" },
  { v: "charity", l: "Charity" }, { v: "online", l: "Online / remote" },
];
const TYPE_LABEL = { ...Object.fromEntries(TYPES.map((t) => [t.v, t.l])), other: "Other service" };

export default function Locator() {
  const { user } = useAuth();
  const [centres, setCentres] = useState([]);
  const [location, setLocation] = useState("");
  const [type, setType] = useState("all");
  const [radius, setRadius] = useState("50");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const search = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/centres", { params: { location: location || undefined, type, radius, q: q || undefined } });
      setCentres(data.centres);
      if (location && data.centres.length === 0) toast.info("No centres found in that radius. Try widening the distance.");
    } catch (e) { toast.error(apiErr(e)); }
    setLoading(false);
  };

  useEffect(() => { search(); }, []); // initial

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-head text-3xl font-bold text-[#2C363F] sm:text-4xl">Find Support Near You</h1>
          <p className="mt-2 text-muted-foreground">Recovery services across the UK & Ireland. Search by postcode or town.</p>
          <p className="mt-2 flex items-start gap-2 text-sm text-[#2C4763]" data-testid="cqc-coverage-note">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#8FB8DE]" />
            <span>England (CQC), Scotland (Care Inspectorate) &amp; Northern Ireland (RQIA) listings are sourced from official regulators. Ireland &amp; Wales listings are currently curated by our team while we seek official data access. Spotted a missing service? Use “Suggest a centre” below.</span>
          </p>
        </div>
        <SuggestDialog onDone={search} />
      </div>

      <div className="rounded-2xl border border-border bg-white p-6 shadow-sm">
        <div className="grid gap-4 sm:grid-cols-4 sm:items-end">
          <div className="sm:col-span-1">
            <Label>Postcode or town</Label>
            <Input data-testid="input-location" value={location} onChange={(e) => setLocation(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="e.g. MK41 or Bedford" className="mt-1 rounded-xl py-6" />
          </div>
          <div>
            <Label>Service type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="select-type" className="mt-1 rounded-xl py-6"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Within</Label>
            <Select value={radius} onValueChange={setRadius}>
              <SelectTrigger data-testid="select-radius" className="mt-1 rounded-xl py-6"><SelectValue /></SelectTrigger>
              <SelectContent>{["10", "25", "50", "100", "250"].map((r) => <SelectItem key={r} value={r}>{r} km</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button data-testid="search-btn" onClick={search} className="rounded-full bg-[#4A7c59] py-6 hover:bg-[#3B6347]"><Search className="mr-2 h-4 w-4" /> Search</Button>
        </div>
      </div>

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2">{[1, 2, 3, 4].map((i) => <div key={i} className="h-40 animate-pulse rounded-2xl bg-muted" />)}</div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2" data-testid="centres-list">
          {centres.map((c) => (
            <div key={c.id} className="rounded-2xl border border-border bg-white p-6 shadow-sm transition-transform duration-300 hover:-translate-y-1">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-head text-lg font-semibold text-[#2C363F]">{c.name}</h3>
                    {c.verified && <span data-testid="verified-badge" className="flex items-center gap-1 rounded-full bg-[#4A7c59]/10 px-2 py-0.5 text-xs font-medium text-[#4A7c59]"><BadgeCheck className="h-3.5 w-3.5" /> Verified</span>}
                    {c.claimed && <span data-testid="managed-badge" className="flex items-center gap-1 rounded-full bg-[#4A7c59]/15 px-2 py-0.5 text-xs font-medium text-[#3B6347]"><Building2 className="h-3.5 w-3.5" /> Managed by provider</span>}
                    {c.source && <span data-testid="source-badge" className="flex items-center gap-1 rounded-full bg-[#8FB8DE]/25 px-2 py-0.5 text-xs font-medium text-[#2C4763]"><ShieldCheck className="h-3.5 w-3.5" /> {c.source}</span>}
                  </div>
                  <span className="mt-1 inline-block rounded-full bg-[#8FB8DE]/20 px-2.5 py-0.5 text-xs font-medium text-[#2C4763]">{TYPE_LABEL[c.type] || c.type}</span>
                </div>
                {c.distance_km != null && <span className="shrink-0 text-sm font-semibold text-[#4A7c59]">{c.distance_km} km</span>}
              </div>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{c.description}</p>
              <div className="mt-4 space-y-1.5 text-sm text-[#2C363F]">
                <div className="flex gap-2"><MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />{c.address}</div>
                {c.phone && <a href={`tel:${c.phone}`} className="flex gap-2 hover:text-[#4A7c59]"><Phone className="h-4 w-4 text-muted-foreground" />{c.phone}</a>}
                {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="flex gap-2 hover:text-[#4A7c59]"><Globe className="h-4 w-4 text-muted-foreground" />Website</a>}
                {c.hours && <div className="flex gap-2"><Clock className="h-4 w-4 text-muted-foreground" />{c.hours}</div>}
              </div>
              {c.lat && (
                <a data-testid="directions-btn" href={`https://www.google.com/maps/dir/?api=1&destination=${c.lat},${c.lng}`} target="_blank" rel="noreferrer"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#4A7c59]/10 px-4 py-2 text-sm font-medium text-[#4A7c59] transition-transform duration-300 hover:-translate-y-0.5"><Navigation className="h-4 w-4" /> Get directions</a>
              )}
              {!c.claimed && (
                <div className="mt-4 border-t border-border pt-3">
                  <ClaimDialog centre={c} user={user} onDone={search} />
                </div>
              )}
            </div>
          ))}
          {centres.length === 0 && <p className="col-span-full py-12 text-center text-muted-foreground">No centres found. Try a different search or suggest one below.</p>}
        </div>
      )}
    </div>
  );
}

function SuggestDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", type: "peer_support", address: "", city: "", postcode: "", phone: "", website: "", hours: "", description: "", country: "UK" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });

  const submit = async () => {
    if (!f.name || !f.address) { toast.error("Please add at least a name and address."); return; }
    try {
      await api.post("/centres/suggest", f);
      toast.success("Thank you! Your suggestion is with our team for review.");
      setOpen(false); setF({ ...f, name: "", address: "", description: "" }); onDone?.();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="suggest-centre-btn" variant="outline" className="rounded-full border-[#4A7c59]/30 text-[#4A7c59]"><Plus className="mr-2 h-4 w-4" /> Suggest a centre</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-2xl">
        <DialogHeader><DialogTitle className="font-head">Suggest a recovery centre</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input data-testid="suggest-name" placeholder="Centre name" value={f.name} onChange={set("name")} className="rounded-xl" />
          <Select value={f.type} onValueChange={set("type")}><SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.filter((t) => t.v !== "all").map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent></Select>
          <Input data-testid="suggest-address" placeholder="Address" value={f.address} onChange={set("address")} className="rounded-xl" />
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Town/City" value={f.city} onChange={set("city")} className="rounded-xl" />
            <Input placeholder="Postcode" value={f.postcode} onChange={set("postcode")} className="rounded-xl" />
          </div>
          <Select value={f.country} onValueChange={set("country")}><SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="UK">UK</SelectItem><SelectItem value="Ireland">Ireland</SelectItem></SelectContent></Select>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Phone" value={f.phone} onChange={set("phone")} className="rounded-xl" />
            <Input placeholder="Website" value={f.website} onChange={set("website")} className="rounded-xl" />
          </div>
          <Input placeholder="Opening hours" value={f.hours} onChange={set("hours")} className="rounded-xl" />
          <Textarea placeholder="Short description" value={f.description} onChange={set("description")} className="rounded-xl" />
          <Button data-testid="suggest-submit" onClick={submit} className="w-full rounded-full bg-[#4A7c59] hover:bg-[#3B6347]">Submit for review</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ClaimDialog({ centre, user, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ contact_name: "", role: "", contact_email: "", phone: "", note: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });

  const canClaim = user && !user.anonymous;

  const submit = async () => {
    if (!f.contact_name || !f.role || !f.contact_email) { toast.error("Please add your name, role and a contact email."); return; }
    setSaving(true);
    try {
      await api.post(`/centres/${centre.id}/claim`, f);
      toast.success("Thanks! Your claim is with our team. We'll verify and get in touch.");
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(apiErr(e)); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`claim-btn-${centre.id}`} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4A7c59] hover:underline">
          <Building2 className="h-4 w-4" /> Is this your centre? Claim it
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-head">Claim {centre.name}</DialogTitle>
          <DialogDescription>Verify that you represent this service so you can keep its details up to date. Our team reviews every claim before it goes live.</DialogDescription>
        </DialogHeader>
        {!canClaim ? (
          <div data-testid="claim-need-account" className="rounded-xl border border-[#8FB8DE]/40 bg-[#8FB8DE]/10 p-4 text-sm text-[#2C4763]">
            You'll need a free account with an email to claim a listing. <Link to={user?.anonymous ? "/app/settings" : "/auth"} className="font-semibold underline">{user?.anonymous ? "Save your account" : "Sign in or create an account"}</Link>, then come back to claim.
          </div>
        ) : (
          <div className="space-y-3">
            <Input data-testid="claim-name" placeholder="Your full name" value={f.contact_name} onChange={set("contact_name")} className="rounded-xl" />
            <Input data-testid="claim-role" placeholder="Your role (e.g. Service Manager)" value={f.role} onChange={set("role")} className="rounded-xl" />
            <Input data-testid="claim-email" type="email" placeholder="Contact email (ideally on your centre's domain)" value={f.contact_email} onChange={set("contact_email")} className="rounded-xl" />
            <Input data-testid="claim-phone" placeholder="Contact phone (optional)" value={f.phone} onChange={set("phone")} className="rounded-xl" />
            <Textarea data-testid="claim-note" placeholder="Anything that helps us verify you (optional)" value={f.note} onChange={set("note")} className="rounded-xl" />
            <Button data-testid="claim-submit" onClick={submit} disabled={saving} className="w-full rounded-full bg-[#4A7c59] hover:bg-[#3B6347]">{saving ? "Submitting…" : "Submit claim"}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

